import { useState, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Modal,
  ModalBody,
  ModalFooter,
  Input,
  FormGroup,
  Label,
  Card,
  CardBody,
  CardHeader,
  Badge,
  ListGroup,
  ListGroupItem,
} from 'reactstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faLayerGroup,
  faClipboardList,
  faCheck,
} from '@fortawesome/free-solid-svg-icons'
import CustomModalHeader from 'components/Custom/CustomModalHeader'
import CustomModalFooter from 'components/Custom/CustomModalFooter'
import SubmitModalButton from 'components/Custom/SubmitModalButton'
import CancelModalButton from 'components/Custom/CancelModalButton'

import { fetchForms, createSection, updateSection } from 'store/Forms/actions'
import Select from '../Select'

const defaultParent = {
  name: 'No Parent (Base Section)',
  value: null,
}

function CreateEditSection({
  showModal,
  setShowModal,
  editMode = false,
  sectionId = null,
  initialData = null,
}) {
  const dispatch = useDispatch()
  const { forms } = useSelector((store) => store.form)

  const [sectionTitle, setSectionTitle] = useState(initialData?.title || '')

  const [selectedForms, setSelectedForms] = useState([])

  // Get sections only (forms with is_section = true)
  const sections = forms?.filter((form) => form.is_section) || []
  // Get forms only (forms with is_section = false)
  const availableForms = useMemo(() => {
    return forms?.filter((form) => !form.is_section) || []
  }, [forms])

  // useEffect(() => {
  //   dispatch(fetchForms())
  // }, [dispatch])

  // Create nested section structure for dropdown display
  const createSectionHierarchy = (sections, parentId = null, level = 0) => {
    const children = sections.filter(
      (section) => section.section_id === parentId
    )
    let result = []

    children.forEach((section) => {
      const prefix = level > 0 ? '-- '.repeat(level) : ''
      result.push({
        ...section,
        displayName: `${prefix}${section.title}`,
        level,
      })
      result = [
        ...result,
        ...createSectionHierarchy(sections, section.id, level + 1),
      ]
    })

    return result
  }

  const hierarchicalSections = createSectionHierarchy(sections)

  // Helper function to get all descendants of a section
  const getDescendants = (sectionId, sections) => {
    const descendants = []
    const children = sections.filter(
      (section) => section.section_id === sectionId
    )

    children.forEach((child) => {
      descendants.push(child.id)
      descendants.push(...getDescendants(child.id, sections))
    })

    return descendants
  }

  // Create options for parent section dropdown
  // Use all sections, not just hierarchical ones to ensure nothing is missed
  const sectionOptions = [
    defaultParent,
    ...sections
      .filter((section) => {
        // Exclude current section and its descendants to prevent circular references
        if (editMode && sectionId) {
          const descendants = getDescendants(sectionId, sections)
          return section.id !== sectionId && !descendants.includes(section.id)
        }
        return section.id !== sectionId
      })
      .map((section) => {
        // Try to find the hierarchical version first for proper indentation
        const hierarchicalSection = hierarchicalSections.find(
          (h) => h.id === section.id
        )
        return {
          name: hierarchicalSection
            ? hierarchicalSection.displayName
            : section.title,
          value: String(section.id),
        }
      }),
  ]

  const [selectedParentSection, setSelectedParentSection] = useState(() => {
    if (!editMode || !initialData) return defaultParent

    return (
      sectionOptions.find(
        (opt) => opt.value === String(initialData.section_id)
      ) || defaultParent
    )
  })

  const handleToggleForm = (formId) => {
    setSelectedForms((prev) =>
      prev.includes(formId)
        ? prev.filter((id) => id !== formId)
        : [...prev, formId]
    )
  }

  const handleSubmit = async () => {
    // Only check title for new sections
    if (!editMode && !sectionTitle.trim()) {
      alert('Please enter a section title')
      return
    }

    try {
      let sectionData = {
        section_id: selectedParentSection.value,
        selectedForms,
      }

      if (editMode) {
        // When editing, include the current title to maintain it
        sectionData.title = initialData?.title
        await dispatch(updateSection(sectionId, sectionData))
      } else {
        // When creating, use the entered title
        sectionData.title = sectionTitle
        await dispatch(createSection(sectionData))
      }

      // Force refresh forms list to show changes immediately
      await dispatch(fetchForms())
      setShowModal(false)
    } catch (error) {
      console.error('Error saving section:', error)
      // alert('Error saving section. Please try again.')
    }
  }

  const getFormDisplayInfo = (form) => {
    if (!form.section_id) return null

    const section = sections.find((s) => s.id === form.section_id)
    return section ? section.title : 'Unknown Section'
  }

  useEffect(() => {
    if (editMode && initialData) {
      setSectionTitle(initialData.title || '')

      // Get forms that belong to this section
      const sectionForms = availableForms
        .filter((form) => form.section_id === sectionId)
        .map((form) => form.id)
      setSelectedForms(sectionForms)
    }
  }, [editMode, initialData, sectionId, availableForms])

  return (
    <Modal
      toggle={() => setShowModal(false)}
      isOpen={showModal}
      centered
      size='lg'
      style={{ maxWidth: '800px' }}
    >
      <CustomModalHeader
        title={editMode ? 'Edit Section' : 'Create Section'}
        toggle={() => setShowModal(false)}
      />

      <ModalBody style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {/* Section Details */}
        <Card className='mb-4'>
          <CardHeader className='bg-primary'>
            <h3 className='text-white mb-0'>Section Details</h3>
          </CardHeader>
          <CardBody>
            {/* Only show title input when creating new section */}
            {!editMode && (
              <FormGroup>
                <Label className='form-control-label font-weight-bold'>
                  Section Title *
                </Label>
                <Input
                  type='text'
                  placeholder='Enter section title'
                  value={sectionTitle}
                  onChange={(e) => setSectionTitle(e.target.value)}
                  required
                />
              </FormGroup>
            )}
            {/* Parent Section Dropdown */}
            <FormGroup>
              <Label className='form-control-label font-weight-bold'>
                Parent Section
              </Label>
              <Select
                isClearable={false}
                options={sectionOptions}
                onChange={(selected) => {
                  if (selected?.option) {
                    setSelectedParentSection(selected.option || null)
                  } else {
                    setSelectedParentSection(defaultParent)
                  }
                }}
                placeholder='Select parent section (optional)'
                multi={false}
                selectedOptions={selectedParentSection}
              />
              <small className='text-muted'>
                Leave empty to create a base section, or select a parent to
                create a nested section
              </small>
            </FormGroup>
          </CardBody>
        </Card>

        {/* Forms Assignment */}
        <Card>
          <CardHeader className='bg-info d-flex align-items-center justify-content-between'>
            <h3 className='text-white mb-0'>Assign Forms to Section</h3>
            <Badge color='light' pill>
              {selectedForms.length} selected
            </Badge>
          </CardHeader>
          <CardBody>
            {availableForms.length === 0 ? (
              <p className='text-muted text-center py-3'>No forms available</p>
            ) : (
              <ListGroup flush>
                {availableForms.map((form) => {
                  const isSelected = selectedForms.includes(form.id)
                  const currentSection = getFormDisplayInfo(form)
                  const isAssignedElsewhere =
                    currentSection &&
                    (!editMode || form.section_id !== sectionId)

                  return (
                    <ListGroupItem
                      key={form.id}
                      className='d-flex align-items-center justify-content-between p-3 border-bottom'
                      style={{
                        backgroundColor: isSelected ? '#e7f3ff' : 'transparent',
                        cursor: 'pointer',
                      }}
                      onClick={(e) => {
                        e.preventDefault()
                        handleToggleForm(form.id)
                      }}
                    >
                      <div className='d-flex align-items-center flex-grow-1'>
                        <div className='custom-control custom-checkbox mr-3'>
                          <input
                            type='checkbox'
                            className='custom-control-input'
                            id={`form-${form.id}`}
                            checked={isSelected}
                            onChange={(e) => {
                              e.stopPropagation()
                              handleToggleForm(form.id)
                            }}
                          />
                          <label
                            className='custom-control-label'
                            htmlFor={`form-${form.id}`}
                          />
                        </div>

                        <FontAwesomeIcon
                          icon={faClipboardList}
                          className='text-primary mr-2'
                        />

                        <div className='flex-grow-1'>
                          <div className='font-weight-bold'>{form.title}</div>
                          {isAssignedElsewhere && (
                            <small className='text-warning'>
                              <FontAwesomeIcon
                                icon={faLayerGroup}
                                className='mr-1'
                              />
                              Currently in: {currentSection}
                            </small>
                          )}
                        </div>
                      </div>

                      {isSelected && (
                        <FontAwesomeIcon
                          icon={faCheck}
                          className='text-success'
                        />
                      )}
                    </ListGroupItem>
                  )
                })}
              </ListGroup>
            )}

            <small className='text-muted mt-2 d-block'>
              Select forms to include in this section. Forms can only belong to
              one section at a time.
            </small>
          </CardBody>
        </Card>
      </ModalBody>

      <CustomModalFooter>
        <SubmitModalButton
          customFn={handleSubmit}
          text={editMode ? 'Update Section' : 'Create Section'}
        />
        <CancelModalButton customFn={() => setShowModal(false)} />
      </CustomModalFooter>
    </Modal>
  )
}

export default CreateEditSection
